import React, { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { startGoogleLogin } from "@/lib/api";
import { Link2, FileText, Youtube, ScrollText, Moon, Sun, ArrowRight, Sparkles, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";
import InstallPWAButton from "@/components/InstallPWAButton";
import PricingSection from "@/components/PricingSection";

const Feature = ({ icon: Icon, title, blurb, testId }) => (
  <div data-testid={testId} className="feature-card group relative rounded-2xl border border-border bg-card p-7">
    <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5">
      <Icon size={20} strokeWidth={1.5} />
    </div>
    <h3 className="text-xl mb-2">{title}</h3>
    <p className="text-sm leading-relaxed text-muted-foreground">{blurb}</p>
  </div>
);

export default function Landing() {
  const { user, loading } = useAuth();
  const { theme, toggle } = useTheme();
  const { t } = useLang();
  const navigate = useNavigate();
  const heroRef = useRef(null);

  // Cursor-follow spotlight over the hero
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty("--mx", `${x}%`);
      el.style.setProperty("--my", `${y}%`);
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <div className="min-h-screen bg-background paper-grain overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="brand-logo">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">
              <Sparkles size={16} strokeWidth={2} />
            </div>
            <span className="font-display text-xl tracking-tight">Readlet</span>
            <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground ml-1">AI</span>
          </div>
          <div className="flex items-center gap-2">
            <InstallPWAButton />
            <LanguageToggle />
            <button
              data-testid="theme-toggle-btn"
              onClick={toggle}
              className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-accent transition-colors duration-200"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {!loading && user ? (
              <Button data-testid="go-to-dashboard-btn" onClick={() => navigate("/dashboard")} className="rounded-full h-10 px-5">
                {t("nav.dashboard")} <ArrowRight size={16} className="ml-1.5" />
              </Button>
            ) : (
              <Button data-testid="signin-btn-nav" onClick={startGoogleLogin} className="rounded-full h-10 px-5">
                {t("nav.signin")}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden">
        {/* Ambient moving light layer */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <span className="orb orb-a" />
          <span className="orb orb-b" />
          <span className="orb orb-c" />
        </div>
        <div className="spotlight" aria-hidden="true" />

        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-muted-foreground mb-6 chip-glow rounded-full px-3 py-1">
              <span className="w-8 h-px bg-primary" /> {t("brand.tagline")}
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-6">
              {t("hero.title.1")}<br />
              <span className="hero-underline-anim italic">{t("hero.title.2")}</span> {t("hero.title.3")}
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mb-9 leading-relaxed">
              {t("hero.body")}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <div className="pulse-primary rounded-full">
                <Button data-testid="hero-signin-btn" onClick={user ? () => navigate("/dashboard") : startGoogleLogin} size="lg" className="rounded-full h-12 px-7 text-base">
                  {user ? t("nav.dashboard") : t("hero.cta")}
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </div>
              <div className="text-sm text-muted-foreground">
                {t("hero.note")}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="rays" aria-hidden="true" />
              <div className="absolute -inset-4 dot-grid opacity-40 rounded-3xl" />
              <div className="relative rounded-3xl border border-border bg-card p-6 shadow-sm">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-primary/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground/40" />
                    <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground/40" />
                  </div>
                  <span className="ml-2 tracking-widest uppercase">Summary preview</span>
                </div>
                <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-2">Main idea</p>
              <p className="text-lg mb-5 leading-snug">Climate change is measurably reducing global crop yields; adaptation must lead policy.</p>
              <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-2">Key findings</p>
                <ul className="space-y-2 text-sm text-foreground/90 mb-5">
                  <li className="flex gap-2"><span className="text-primary">01</span> Temperatures rose 1.5°C over the study window.</li>
                  <li className="flex gap-2"><span className="text-primary">02</span> Rice production fell 12% in affected regions.</li>
                  <li className="flex gap-2"><span className="text-primary">03</span> Water shortages remain the dominant factor.</li>
                </ul>
                <div className="flex items-center justify-between pt-4 border-t border-border text-xs text-muted-foreground">
                  <span>Reading time · 25 min</span>
                  <span className="text-primary font-semibold">Summary · 1 min</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider max-w-7xl mx-auto" />

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-14">
          <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold mb-3">Four ways in</p>
          <h2 className="text-4xl sm:text-5xl leading-tight mb-4">Everything you read, distilled.</h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Whether it&apos;s a research paper you&apos;ve been avoiding or a keynote you missed — paste, upload, or link. Readlet reads it in seconds.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Feature testId="feature-url" icon={Link2} title="Any URL" blurb="Blog posts, news articles, research pages — pull the substance without the sidebars." />
          <Feature testId="feature-pdf" icon={FileText} title="PDFs" blurb="Contracts, papers, reports. Upload up to 15 MB and we'll parse every page." />
          <Feature testId="feature-text" icon={ScrollText} title="Pasted text" blurb="A stubborn passage? Paste it in and get an instant, faithful digest." />
          <Feature testId="feature-youtube" icon={Youtube} title="YouTube" blurb="Give us a link, we lift the transcript and hand back the takeaways." />
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="rounded-3xl border border-border bg-card p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl md:text-4xl mb-2">Ready when you are.</h2>
            <p className="text-muted-foreground">Sign in with Google and try your first summary in under a minute.</p>
          </div>
          <Button data-testid="cta-signin-btn" onClick={user ? () => navigate("/dashboard") : startGoogleLogin} size="lg" className="rounded-full h-12 px-7 pulse-primary">
            {user ? "Open dashboard" : "Sign in with Google"}
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} Readlet</span>
          <span className="tracking-widest uppercase text-xs">Read less · Understand more</span>
        </div>
      </footer>
    </div>
  );
}
